export const SIGNUP_FLOW_KEY = 'culineer_signup_flow'
export const POST_ONBOARDING_REDIRECT_KEY = 'post_onboarding_redirect'
const PENDING_GENERAL_KEY = 'pending_general_account'
const SAW_PRICING_KEY = 'pending_general_saw_pricing'

const FRESH_SIGNUP_WINDOW_MS = 10 * 60 * 1000

export type PendingGeneralAccount =
  | { source: 'email'; first_name: string; last_name: string; email: string; password: string }
  | { source: 'social' }

export const startSignupFlow = (): void => {
  try {
    sessionStorage.setItem(SIGNUP_FLOW_KEY, '1')
  } catch (err) {
    console.error('Failed to start signup flow:', err)
  }
}

export const isSignupFlowActive = (): boolean => {
  try {
    return sessionStorage.getItem(SIGNUP_FLOW_KEY) === '1'
  } catch (err) {
    console.error('Failed to read signup flow:', err)
    return false
  }
}

export const endSignupFlow = (): void => {
  try {
    sessionStorage.removeItem(SIGNUP_FLOW_KEY)
  } catch (err) {
    console.error('Failed to end signup flow:', err)
  }
}

export const setPostOnboardingRedirect = (url: string): void => {
  try {
    sessionStorage.setItem(POST_ONBOARDING_REDIRECT_KEY, url)
  } catch (err) {
    console.error('Failed to store post-onboarding redirect:', err)
  }
}

export const consumePostOnboardingRedirect = (): string => {
  try {
    const url = sessionStorage.getItem(POST_ONBOARDING_REDIRECT_KEY)
    if (url) sessionStorage.removeItem(POST_ONBOARDING_REDIRECT_KEY)
    return url || '/discover'
  } catch (err) {
    console.error('Failed to read post-onboarding redirect:', err)
    return '/discover'
  }
}

type SignupUser = {
  date_created?: string | null
  date_onboarding_completed?: string | null
  user_type?: string | null
  default_listing_id?: number | null
}

export const isFreshSocialSignup = (user?: SignupUser | null): boolean => {
  if (!user) return false
  if (user.date_onboarding_completed) return false
  if (user.default_listing_id) return false
  const type = (user.user_type || 'General').toLowerCase()
  if (type !== 'general') return false
  if (!user.date_created) return false
  const created = new Date(user.date_created).getTime()
  if (Number.isNaN(created)) return false
  return Date.now() - created < FRESH_SIGNUP_WINDOW_MS
}

export const stashPendingGeneralAccount = (account: PendingGeneralAccount): void => {
  try {
    sessionStorage.setItem(PENDING_GENERAL_KEY, JSON.stringify(account))
    sessionStorage.removeItem(SAW_PRICING_KEY)
  } catch (err) {
    console.error('Failed to stash pending general account:', err)
  }
}

export const peekPendingGeneralAccount = (): PendingGeneralAccount | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_GENERAL_KEY)
    return raw ? (JSON.parse(raw) as PendingGeneralAccount) : null
  } catch {
    return null
  }
}

export const consumePendingGeneralAccount = (): PendingGeneralAccount | null => {
  const pending = peekPendingGeneralAccount()
  try {
    sessionStorage.removeItem(PENDING_GENERAL_KEY)
    sessionStorage.removeItem(SAW_PRICING_KEY)
  } catch (err) {
    console.error('Failed to clear pending general account:', err)
  }
  return pending
}

export const markSawBusinessPricing = (): void => {
  try {
    if (sessionStorage.getItem(PENDING_GENERAL_KEY)) {
      sessionStorage.setItem(SAW_PRICING_KEY, '1')
    }
  } catch {
    // ignore
  }
}

export const hasSawBusinessPricing = (): boolean => {
  try {
    return sessionStorage.getItem(SAW_PRICING_KEY) === '1'
  } catch {
    return false
  }
}

export const isBusinessRegistrationPath = (pathname: string): boolean => {
  return (
    pathname === '/pricing' ||
    pathname === '/business-onboarding' ||
    pathname.startsWith('/form') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/account-upgrade') ||
    pathname.startsWith('/welcome')
  )
}
